import _ from 'lodash'
import Guard from 'arg-guard'

import LocalDatabase from './local-database'
import Logger from '@/logger'

/**
 * Base for a local storage gateway.
 *
 * @export LocalStorage
 * @class LocalStorage
 */
export default class LocalStorage {

    /**
     * Creates an instance of LocalStorage.
     * @param {string} [table='context'] An optional store name.
     * @param {Object} [keys=[]] The array of keys used to access data.
     * @param {Type} RemoteStoreCopyType The type of the remote store class.
     *
     * @memberof LocalStorage
     */
    constructor({ table = 'context', keys = [], useSessionStorage = false }) {
        this.db = new LocalDatabase(table, useSessionStorage)
        this.table = table
        this.keys = keys
    }

    /**
     * Clears all of the associated data from this storage.
     *
     * @param {Context} [authContext=null]
     * @returns {Promise<boolean>} Indicates the success of the operation.
     * @memberof LocalStorage
     */
    async clearAsync(authContext = null) {
        try {
            await this.keys.forEachAsync(async key => {
                if (authContext)
                    await this.db[this.table].removeItem(this.formatContextualKey(key, authContext))
                else
                    await this.db[this.table].removeItem(key)

                Logger.logObject(authContext, `Data cleared [${ key }]`, 'Local Storage')
            })

            return true
        } catch (err) {
            Logger.error(err)
            return false
        }
    }

    /**
     * Clones all of the associated data from this storage to attach to a new context.
     *
     * @param {Context} authContext
     * @param {string} newContextId
     * @returns {Promise<boolean>} Indicates the success of the operation.
     * @memberof LocalStorage
     */
    async cloneAsync(authContext, newContextId) {
        Guard.throwIf(authContext, 'authContext')
        Guard.throwIf(newContextId, 'newContextId')

        try {
            let dataCloned = false
            await this.keys.forEachAsync(async key => {
                const data = await this.getValueAsync(this.formatContextualKey(key, authContext))
                if (data) {
                    const value = await this.upsertDataAsync(`${ newContextId }-${ key }`, data, { syncRemote: false })
                    Logger.logObject(value, `Data cloned [${ this.table }].[${ key }] to ${ newContextId }`, 'Local Storage')
                    dataCloned = true
                }

                Logger.log(`No data to clone [${ this.table }].[${ key }] to ${ newContextId }`, 'Local Storage')
            })

            return dataCloned
        } catch (err) {
            Logger.error(err)
            return false
        }
    }

    /**
     * Formats a contextual data key to allow multiple users to share the data store.
     *
     * @param {string} key
     * @param {Context} authContext
     * @returns {string} The formatted context key to access the data item.
     * @memberof LocalStorage
     */
    formatContextualKey(key, authContext) {
        return authContext && authContext.type != 'guest'
            ? `${ authContext.id }-${ key }`
            : `guest-${ key }`
    }

    /**
     * Gets the selected data from local storage if there is one.
     *
     * @param {string} key
     * @param {Object} [{ ReturnType }={ ReturnType: undefined }]
     * @param {Class} ReturnType The object type to return a specific object type. The object must implement a 'fromStorageJson()' method or the raw data will be returned.
     * @returns {Promise<ReturnType>} The value
     * @memberof LocalStorage
     */
    async getValueAsync(key, { ReturnType } = { ReturnType: undefined }) {
        Guard.throwIf(key, 'key')

        try {
            let value = await this.db[this.table].getItem(key)
            if (ReturnType && _.isFunction(ReturnType.fromStoreJson)) value = ReturnType.fromStoreJson(value)
            Logger.logObject(value, `Data loaded [${ this.table }].[${ key }]`, 'Local Storage')
            return value
        } catch (err) {
            throw new Error(`Unable to get '${ key }': ${ err }`, 'Local Storage')
        }
    }

    /**
     * Removes data from local storage.
     *
     * @param {string} key
     * @returns {Promise<boolean>} The removal response.
     * @memberof LocalStorage
     */
    async removeDataAsync(key) {
        Guard.throwIf(key, 'key')

        try {
            const existingValue = await this.db[this.table].getItem(key)
            if (existingValue) {
                await this.db[this.table].removeItem(key)
                Logger.logObject(key, `Data removed [${ this.table }].[${ key }]`, 'Local Storage')
                return true
            }
            return false
        } catch (err) {
            throw new Error(`Unable to remove '${ key }': ${ err }`, 'Local Storage')
        }
    }

    /**
     * Inserts or updates data to local storage.
     *
     * @param {string} key
     * @param {Object} data
     * @returns {Promise<Object>} The data upserted as stored.
     * @memberof LocalStorage
     */
    async upsertDataAsync(key, data) {
        Guard.throwIf(key, 'key')
        Guard.throwIf(data, 'data')

        try {
            if (_.isFunction(data.toStoreJson)) data = data.toStoreJson()
            const existingValue = await this.db[this.table].getItem(key)
            if (_.isEqual(data, existingValue)) return existingValue

            const value = await this.db[this.table].setItem(key, data)
            Logger.logObject(value, `Data persisted [${ this.table }].[${ key }]`, 'Local Storage')
            return value
        } catch (err) {
            throw new Error(`Unable to save '${ key }': ${ err }`, 'Local Storage')
        }
    }
}
